/**
 * ODX Cinema 2 — translate track + optional infinite loop (prepended/appended clones in JS).
 * Physical index = position on strip; public goToSlide(i) uses logical 0..N-1 (thumbnails, registry).
 *
 * Released playback hold (**released slides only**): when `odx_c2_hold_released_playback`, released slides use
 * teaser still + video inside `.odx-cinema2__upcoming-overlay--released-hold-video` plus mute / play / fullscreen
 * controls on the same `odx-cinema-ui-layer.odx-cinema2__transport` (unreleased markup unchanged). `_syncSlideVideos` uses
 * `video.odx-cinema2__media` on the active slide. Mute pair = **state** (muted → strike; unmuted → waves).
 * Play / fullscreen pairs stay **action** (tap target) affordance with optional GSAP when `window.gsap` exists.
 * After `video.play()`, UI sync is deferred until the returned Promise settles (or rAF), because `paused`
 * can still be true in the same synchronous turn; `play` / `pause` / `volumechange` on the track keep glyphs aligned.
 * `.odx-cinema2__controls` is slideshow prev/next only, not this transport stack.
 */
(function () {
  "use strict";

  var SWIPE_MIN_PX = 48;
  var SWIPE_DOMINANCE = 1.15;
  /** GR419: side tap zones — keep clear of corner controls */
  var STAGE_NAV_EDGE = 0.15;
  var TOUCH_OPTS = { passive: true, capture: true };
  var THUMB_CELL = 40;
  var THUMB_GAP = 8;
  /** Fallback if viewport has no layout yet; ~inner width of 280px pill (border-box minus pad + border). */
  var REMOTE_THUMB_VIEWPORT_FALLBACK = 266;
  var CLONE_MAX = 3;
  var docKeyNavInstalled = false;
  /** GR425: ms of pointer/keyboard idle before chrome hides (override with `data-odx-immersive-ms` on `<odx-cinema2-carousel>`). */
  var IMMERSIVE_IDLE_MS_DEFAULT = 4000;
  var IMMERSIVE_PTR_OPTS = { passive: true };

  /** GR410: single largest unit (days → hours → minutes); ALL CAPS; past target → UNLOCKED (Chillax + tabular-nums in CSS). */
  function formatOdxC2UpcomingSignificant(diffMs) {
    if (diffMs <= 0) return "UNLOCKED";
    var d = Math.floor(diffMs / 86400000);
    var h = Math.floor((diffMs % 86400000) / 3600000);
    var m = Math.floor((diffMs % 3600000) / 60000);
    var s;
    if (d > 0) s = d + " DAY" + (d === 1 ? "" : "S");
    else if (h > 0) s = h + " HOUR" + (h === 1 ? "" : "S");
    else if (m > 0) s = m + " MINUTE" + (m === 1 ? "" : "S");
    else s = "SOON";
    return String(s).toUpperCase();
  }

  function tickOdxC2UpcomingSignificant(track) {
    if (!track || (typeof document !== "undefined" && document.hidden)) return;
    var nodes = track.querySelectorAll("[data-odx-c2-upcoming-countdown]");
    var now = Date.now();
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var raw = el.getAttribute("data-odx-c2-upcoming-countdown");
      var sec = raw == null ? NaN : parseInt(raw, 10);
      if (sec !== sec) continue;
      var diff = sec * 1000 - now;
      el.textContent = formatOdxC2UpcomingSignificant(diff);
    }
  }

  function installDocumentArrowNav() {
    if (docKeyNavInstalled) return;
    docKeyNavInstalled = true;
    document.addEventListener(
      "keydown",
      function (e) {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        var sec =
          (e.target && e.target.closest && e.target.closest("[data-odx-cinema2]")) ||
          (document.activeElement &&
            document.activeElement.closest &&
            document.activeElement.closest("[data-odx-cinema2]"));
        if (!sec || sec.getAttribute("data-odx-cinema2-wired") !== "1") return;
        var carousel = sec.querySelector("odx-cinema2-carousel");
        if (!carousel || !carousel.track) return;
        if (typeof carousel.refreshSlidesFromDom === "function") {
          carousel.refreshSlidesFromDom();
        }
        var nReal = carousel.originalSlideCount != null ? carousel.originalSlideCount : carousel.slides.length;
        if (nReal < 2) return;
        e.preventDefault();
        if (e.key === "ArrowRight") carousel.next();
        else carousel.prev();
      },
      false,
    );
  }

  /**
   * Swap a transport glyph pair (hide `elOut`, show `elIn`). Used for mute **state**, play / fs **actions**.
   * When `window.gsap` exists (Cinema 2 section loads GSAP before this file), `useAnim` fades the incoming glyph in.
   * Uses set/remove `hidden` on the DOM (plus `.hidden`) so SVGs toggle reliably; clears GSAP autoAlpha leftovers before swap.
   */
  function odxC2GlyphHidden(el, hidden) {
    if (!el) return;
    try {
      if (hidden) el.setAttribute("hidden", "");
      else el.removeAttribute("hidden");
    } catch (eH) {
      /* ignore */
    }
    try {
      el.hidden = !!hidden;
    } catch (eH2) {
      /* ignore */
    }
  }

  function odxC2SwapTransportGlyphs(elOut, elIn, useAnim) {
    var g = typeof window !== "undefined" && window.gsap;
    if (g) {
      try {
        if (typeof g.killTweensOf === "function") {
          if (elOut) g.killTweensOf(elOut);
          if (elIn) g.killTweensOf(elIn);
        }
        if (typeof g.set === "function") {
          if (elOut) g.set(elOut, { clearProps: "opacity,visibility" });
          if (elIn) g.set(elIn, { clearProps: "opacity,visibility" });
        }
      } catch (eKill) {
        /* ignore */
      }
    } else {
      try {
        if (elOut && elOut.style) {
          elOut.style.removeProperty("opacity");
          elOut.style.removeProperty("visibility");
        }
        if (elIn && elIn.style) {
          elIn.style.removeProperty("opacity");
          elIn.style.removeProperty("visibility");
        }
      } catch (eClr) {
        /* ignore */
      }
    }
    odxC2GlyphHidden(elOut, true);
    if (!elIn) return;
    odxC2GlyphHidden(elIn, false);
    if (!useAnim) return;
    if (g && typeof g.fromTo === "function") {
      try {
        g.fromTo(elIn, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.16, ease: "power2.out", overwrite: "auto" });
      } catch (eG) {
        /* ignore */
      }
    }
  }

  /** Cross-vendor fullscreen element for glyph sync (Safari / legacy). */
  function odxC2GetFullscreenElement() {
    var d = typeof document !== "undefined" ? document : null;
    if (!d) return null;
    return (
      d.fullscreenElement ||
      d.webkitFullscreenElement ||
      d.mozFullScreenElement ||
      d.msFullscreenElement ||
      null
    );
  }

  /**
   * Slide-scoped BEM mirror on `.odx-cinema2__transport` (wraps play/mute/fs in Liquid).
   * Not `.odx-cinema2__controls` — that node is slideshow prev/next only (`data-odx-cinema2-nav`).
   * Buttons keep `is-playing` / `is-muted` for a11y + glyph swap; `[data-odx-cinema2]` keeps `is-fullscreen`.
   */
  function odxC2MirrorTransportBem(slide, v) {
    if (!slide) return;
    var wrap = slide.querySelector(".odx-cinema2__transport");
    if (!wrap) return;
    if (!v) {
      wrap.classList.remove("is-playing", "is-muted");
      return;
    }
    var playing = !v.paused && !v.ended;
    wrap.classList.toggle("is-playing", playing);
    wrap.classList.toggle("is-muted", !!v.muted);
  }

  class OdxCinema2Carousel extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute("data-odx-cinema2-carousel-init") === "1") return;
      this.setAttribute("data-odx-cinema2-carousel-init", "1");
      var self = this;
      this.track = this.querySelector("[data-odx-cinema2-track]");
      this.stage = this.querySelector(".odx-cinema2__stage");
      this.slides = [];
      this.current = 0;
      this.cloneLead = 0;
      this.originalSlideCount = 0;
      this._seedInfiniteStart = true;
      this._onResize = this.refreshLayout.bind(this);
      this._touchStart = this._onTouchStart.bind(this);
      this._touchEnd = this._onTouchEnd.bind(this);
      this._stageClick = this._onStageClick.bind(this);
      this._onTrackTransitionEnd = function (e) {
        if (e.target !== self.track || e.propertyName !== "transform") return;
        self._maybeSnapInfinite();
      };
      this._touchX0 = 0;
      this._touchY0 = 0;
      this._tick = null;
      this._autoplay = this.getAttribute("data-odx-autoplay") === "1";
      this._intervalMs = parseInt(this.getAttribute("data-odx-interval-ms") || "6000", 10) || 6000;
      this._onVis = this._onVisibilityChange.bind(this);
      this._onEnter = this._pauseAutoplay.bind(this);
      this._onLeave = this._resumeAutoplay.bind(this);
      this._trackTouchBound = false;
      this._trackTransitionBound = false;
      this._stageClickBound = false;
      this._stageHoverBound = false;
      this._visListenBound = false;
      this._upcomingSigInv = null;
      this.isVideoPlaying = false;
      this._autoplaySuspendedUntilNav = false;
      this._videoInterlockInstalled = false;
      this._videoInterlockFn = null;
      this._immersiveTimer = null;
      this._immersiveListeners = false;
      this._onImmersivePointer = null;
      this._onImmersiveKey = null;
      this._immersiveIdleMs = IMMERSIVE_IDLE_MS_DEFAULT;

      this._onNavPrevClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.prev();
      };
      this._onNavNextClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.next();
      };
      this._navPrev = this.querySelector("[data-odx-cinema2-prev]");
      this._navNext = this.querySelector("[data-odx-cinema2-next]");
      if (this._navPrev) this._navPrev.addEventListener("click", this._onNavPrevClick, false);
      if (this._navNext) this._navNext.addEventListener("click", this._onNavNextClick, false);

      if (this.track && !this._trackTransitionBound) {
        this.track.addEventListener("transitionend", this._onTrackTransitionEnd, false);
        this._trackTransitionBound = true;
      }

      this._installVideoAutoplayInterlock();

      this._section = this.closest("[data-odx-cinema2]");
      this._fullscreenUiBound = false;
      this._onFullscreenChangeForC2 = function () {
        self._syncAllFsGlyphs(true);
      };
      if (typeof document !== "undefined" && this._section) {
        document.addEventListener("fullscreenchange", this._onFullscreenChangeForC2, false);
        document.addEventListener("webkitfullscreenchange", this._onFullscreenChangeForC2, false);
        this._fullscreenUiBound = true;
      }
      this._thumbTrack = null;
      this._thumbViewport = null;
      this._onThumbStripClick = function (e) {
        var btn = e.target && e.target.closest && e.target.closest("[data-odx-cinema2-thumb]");
        if (!btn || !self._section || !self._section.contains(btn)) return;
        var raw = btn.getAttribute("data-slide-index");
        var i = raw == null ? NaN : parseInt(raw, 10);
        if (i !== i || i < 0) return;
        e.preventDefault();
        self.goToSlide(i);
      };
      if (this._section) {
        this._section.addEventListener("click", this._onThumbStripClick, false);
      }

      this._setupImmersiveMode();

      this._cinemaUiClickBound = false;
      this._onCinemaUiClick = function (e) {
        self._handleCinemaUiClick(e);
      };
      if (this._section) {
        this._section.addEventListener("click", this._onCinemaUiClick, false);
        this._cinemaUiClickBound = true;
      }

      this._mediaWrapTapBound = false;
      this._onMediaWrapTap = function (e) {
        self._handleMediaWrapTap(e);
      };
      this.addEventListener("click", this._onMediaWrapTap, true);
      this._mediaWrapTapBound = true;

      this.refreshLayout();

      if (typeof ResizeObserver !== "undefined" && this.track && this.track.parentElement) {
        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(this.track.parentElement);
      } else if (typeof window !== "undefined") {
        window.addEventListener("resize", this._onResize, false);
      }

      var sec = this.closest("[data-odx-cinema2]");
      if (sec) {
        setTimeout(function () {
          wireSection(sec);
        }, 0);
      }
      setTimeout(function () {
        self.refreshLayout();
        if (typeof self._syncAllFsGlyphs === "function") self._syncAllFsGlyphs(false);
      }, 50);
    }

    disconnectedCallback() {
      this._teardownImmersiveMode();
      this._teardownVideoAutoplayInterlock();
      if (this._mediaWrapTapBound && this._onMediaWrapTap) {
        this.removeEventListener("click", this._onMediaWrapTap, true);
        this._mediaWrapTapBound = false;
      }
      this._onMediaWrapTap = null;
      if (this._section && this._cinemaUiClickBound && this._onCinemaUiClick) {
        this._section.removeEventListener("click", this._onCinemaUiClick, false);
        this._cinemaUiClickBound = false;
      }
      this._onCinemaUiClick = null;
      this._stopUpcomingSignificantCountdown();
      this._stopAutoplay();
      if (this._visListenBound) {
        document.removeEventListener("visibilitychange", this._onVis, false);
        this._visListenBound = false;
      }
      this._teardownStageInteractions();
      if (this._fullscreenUiBound && typeof document !== "undefined" && this._onFullscreenChangeForC2) {
        document.removeEventListener("fullscreenchange", this._onFullscreenChangeForC2, false);
        document.removeEventListener("webkitfullscreenchange", this._onFullscreenChangeForC2, false);
        this._fullscreenUiBound = false;
      }
      this._onFullscreenChangeForC2 = null;
      if (this.track && this._trackTransitionBound) {
        this.track.removeEventListener("transitionend", this._onTrackTransitionEnd, false);
        this._trackTransitionBound = false;
      }
      if (this._navPrev) {
        this._navPrev.removeEventListener("click", this._onNavPrevClick, false);
      }
      if (this._navNext) {
        this._navNext.removeEventListener("click", this._onNavNextClick, false);
      }
      if (this._section && this._onThumbStripClick) {
        this._section.removeEventListener("click", this._onThumbStripClick, false);
      }
      this._section = null;
      this._thumbTrack = null;
      this._thumbViewport = null;
      if (this._ro && this.track && this.track.parentElement) {
        this._ro.unobserve(this.track.parentElement);
      }
      this._ro = null;
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", this._onResize, false);
      }
    }

    /** Re-query slides from the track (includes clones once built). */
    refreshSlidesFromDom() {
      this.track = this.track || this.querySelector("[data-odx-cinema2-track]");
      if (!this.track) {
        this.slides = [];
        return;
      }
      this.slides = [].slice.call(this.track.querySelectorAll(".odx-cinema2__slide"));
    }

    _removeInfiniteClones() {
      if (!this.track) return;
      [].slice.call(this.track.querySelectorAll("[data-odx-clone]")).forEach(function (n) {
        n.remove();
      });
    }

    /**
     * Clone last k roots before the strip and first k after (k = min(3,N)); keeps one Liquid source of truth.
     */
    _buildInfiniteClones() {
      this._removeInfiniteClones();
      this.refreshSlidesFromDom();
      var track = this.track;
      if (!track) return;
      var roots = [].slice.call(track.querySelectorAll(".odx-cinema2__slide:not([data-odx-clone])"));
      var N = roots.length;
      this.originalSlideCount = N;
      if (N < 2) {
        this.cloneLead = 0;
        return;
      }
      var k = Math.min(CLONE_MAX, N);
      this.cloneLead = k;
      var i;
      for (i = N - k; i < N; i++) {
        var pre = roots[i].cloneNode(true);
        pre.setAttribute("data-odx-clone", "pre");
        pre.removeAttribute("id");
        track.insertBefore(pre, track.firstChild);
      }
      for (i = 0; i < k; i++) {
        var post = roots[i].cloneNode(true);
        post.setAttribute("data-odx-clone", "post");
        post.removeAttribute("id");
        track.appendChild(post);
      }
      this.refreshSlidesFromDom();
      this._pauseVideosInClones();
      this._resyncCountdownTimersInClones();
      this._syncAllFsGlyphs(false);
    }

    _stopUpcomingSignificantCountdown() {
      if (this._upcomingSigInv) {
        clearInterval(this._upcomingSigInv);
        this._upcomingSigInv = null;
      }
    }

    _startUpcomingSignificantCountdown() {
      this._stopUpcomingSignificantCountdown();
      if (!this.track) return;
      if (!this.track.querySelector("[data-odx-c2-upcoming-countdown]")) return;
      var tr = this.track;
      var tick = function () {
        tickOdxC2UpcomingSignificant(tr);
      };
      tick();
      this._upcomingSigInv = setInterval(tick, 1000);
    }

    /**
     * cloneNode(true) duplicates light DOM; <odx-countdown-timer> may need a reconnect so
     * connectedCallback runs per clone (intervals + cascade state). Only touches nodes inside clones.
     */
    _resyncCountdownTimersInClones() {
      if (!this.track) return;
      [].slice
        .call(this.track.querySelectorAll("[data-odx-clone] odx-countdown-timer"))
        .forEach(function (el) {
          var p = el.parentNode;
          if (!p) return;
          var next = el.nextSibling;
          p.removeChild(el);
          if (next) p.insertBefore(el, next);
          else p.appendChild(el);
        });
    }

    _pauseVideosInClones() {
      if (!this.track) return;
      [].slice.call(this.track.querySelectorAll("[data-odx-clone] video.odx-cinema2__media")).forEach(function (v) {
        try {
          v.pause();
        } catch (e) {
          /* ignore */
        }
      });
    }

    physicalToLogical(p) {
      var k = this.cloneLead;
      var n = this.originalSlideCount;
      if (!k || !n) return p;
      if (p < k) return n - k + p;
      if (p < k + n) return p - k;
      return p - k - n;
    }

    getLogicalIndex() {
      return this.physicalToLogical(this.current);
    }

    _teardownStageInteractions() {
      if (this.stage) {
        this.stage.removeEventListener("mouseenter", this._onEnter, false);
        this.stage.removeEventListener("mouseleave", this._onLeave, false);
        this.stage.removeEventListener("click", this._stageClick, false);
      }
      this._stageHoverBound = false;
      this._stageClickBound = false;
      if (this.track && this._trackTouchBound) {
        this.track.removeEventListener("touchstart", this._touchStart, TOUCH_OPTS);
        this.track.removeEventListener("touchend", this._touchEnd, TOUCH_OPTS);
      }
      this._trackTouchBound = false;
    }

    _ensureNavAndInteractions() {
      var nReal = this.originalSlideCount || this.slides.length;
      var nav = this.querySelector("[data-odx-cinema2-nav]");
      if (nav) {
        if (nReal > 1) nav.removeAttribute("hidden");
        else nav.setAttribute("hidden", "");
      }

      if (nReal > 1) {
        if (this.track && !this._trackTouchBound) {
          this.track.addEventListener("touchstart", this._touchStart, TOUCH_OPTS);
          this.track.addEventListener("touchend", this._touchEnd, TOUCH_OPTS);
          this._trackTouchBound = true;
        }
        if (this.stage && !this._stageClickBound) {
          this.stage.addEventListener("click", this._stageClick, false);
          this._stageClickBound = true;
        }
        if (this.stage && this._autoplay && !this._stageHoverBound) {
          this.stage.addEventListener("mouseenter", this._onEnter, false);
          this.stage.addEventListener("mouseleave", this._onLeave, false);
          this._stageHoverBound = true;
        }
      } else {
        this._teardownStageInteractions();
      }
    }

    _ensureAutoplayListeners() {
      var nReal = this.originalSlideCount || this.slides.length;
      if (!this._autoplay) {
        this._stopAutoplay();
        if (this._visListenBound) {
          document.removeEventListener("visibilitychange", this._onVis, false);
          this._visListenBound = false;
        }
        return;
      }
      if (nReal < 2) {
        this._stopAutoplay();
        if (this._visListenBound) {
          document.removeEventListener("visibilitychange", this._onVis, false);
          this._visListenBound = false;
        }
        return;
      }
      if (!this._visListenBound) {
        document.addEventListener("visibilitychange", this._onVis, false);
        this._visListenBound = true;
      }
      this._restartAutoplayIfNeeded();
    }

    _onTouchStart(e) {
      var t = (e.changedTouches && e.changedTouches[0]) || (e.touches && e.touches[0]);
      if (!t) return;
      this._touchX0 = t.clientX;
      this._touchY0 = t.clientY;
    }

    _onTouchEnd(e) {
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - this._touchX0;
      var dy = t.clientY - this._touchY0;
      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE) return;
      if (dx < 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    _handleCinemaUiClick(e) {
      var muteBtn = e.target && e.target.closest && e.target.closest("[data-odx-c2-ui-mute]");
      var playBtn = e.target && e.target.closest && e.target.closest("[data-odx-c2-ui-play]");
      var fsBtn = e.target && e.target.closest && e.target.closest("[data-odx-c2-ui-fs]");
      if (!muteBtn && !playBtn && !fsBtn) return;
      e.preventDefault();
      e.stopPropagation();
      var section = this._section || this.closest("[data-odx-cinema2]");
      if (fsBtn) {
        if (!section) return;
        var selfFs = this;
        var bumpFsUi = function () {
          if (typeof selfFs._syncAllFsGlyphs === "function") selfFs._syncAllFsGlyphs(false);
        };
        var fsEl0 = odxC2GetFullscreenElement();
        if (fsEl0 === section) {
          var d0 = document;
          var pExit = null;
          try {
            if (typeof d0.exitFullscreen === "function") pExit = d0.exitFullscreen();
            else if (typeof d0.webkitExitFullscreen === "function") pExit = d0.webkitExitFullscreen();
            else if (typeof d0.msExitFullscreen === "function") pExit = d0.msExitFullscreen();
          } catch (eX) {
            pExit = null;
          }
          if (pExit && typeof pExit.then === "function") {
            pExit.then(bumpFsUi, bumpFsUi);
          } else {
            if (typeof window.requestAnimationFrame === "function") {
              window.requestAnimationFrame(function () {
                window.requestAnimationFrame(bumpFsUi);
              });
            } else {
              window.setTimeout(bumpFsUi, 0);
            }
          }
        } else {
          var reqFs =
            section.requestFullscreen ||
            section.webkitRequestFullscreen ||
            section.mozRequestFullScreen ||
            section.msRequestFullscreen;
          if (reqFs) {
            try {
              var pFs = reqFs.call(section);
              if (pFs && typeof pFs.then === "function") {
                pFs.then(
                  function () {
                    selfFs._syncAllFsGlyphs(true);
                  },
                  function () {
                    bumpFsUi();
                  },
                );
              } else if (typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(function () {
                  window.requestAnimationFrame(bumpFsUi);
                });
              } else {
                window.setTimeout(bumpFsUi, 0);
              }
            } catch (eFs) {
              bumpFsUi();
            }
          } else {
            bumpFsUi();
          }
        }
        return;
      }
      var slide =
        (muteBtn && muteBtn.closest && muteBtn.closest(".odx-cinema2__slide")) ||
        (playBtn && playBtn.closest && playBtn.closest(".odx-cinema2__slide"));
      if (!slide || slide !== this.slides[this.current]) return;
      var v = slide.querySelector("video.odx-cinema2__media");
      if (!v) return;
      if (muteBtn) {
        try {
          v.muted = !v.muted;
        } catch (err) {
          /* ignore */
        }
        muteBtn.classList.toggle("is-muted", !!v.muted);
        this._syncCinema2MuteGlyphs(muteBtn, v, true);
        odxC2MirrorTransportBem(slide, v);
        return;
      }
      if (!playBtn) return;
      var self = this;
      var didRequestPlay = false;
      var playPromise = null;
      try {
        if (v.paused || v.ended) {
          if (v.ended) v.currentTime = 0;
          playPromise = v.play();
          didRequestPlay = true;
          if (playPromise && typeof playPromise.then === "function") {
            playPromise.then(
              function () {
                self._syncCinema2UiPlayState(true);
              },
              function () {
                self._syncCinema2UiPlayState(true);
              },
            );
          }
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(function () {});
          }
        } else {
          v.pause();
        }
      } catch (err2) {
        didRequestPlay = false;
      }
      if (!didRequestPlay) {
        this._syncCinema2UiPlayState(true);
      } else if (!playPromise || typeof playPromise.then !== "function") {
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(function () {
            self._syncCinema2UiPlayState(true);
          });
        } else {
          window.setTimeout(function () {
            self._syncCinema2UiPlayState(true);
          }, 0);
        }
      }
    }

    _handleMediaWrapTap(e) {
      if (
        e.target &&
        e.target.closest &&
        e.target.closest(
          "[data-odx-c2-ui-play],[data-odx-c2-ui-fs],[data-odx-c2-ui-mute],[data-odx-cinema2-nav],a,button,[role='button'],[data-odx-cinema2-thumb]",
        )
      ) {
        return;
      }
      var st = this.stage;
      if (st && st.getBoundingClientRect) {
        var r = st.getBoundingClientRect();
        var tw = r.width > 0 ? (e.clientX - r.left) / r.width : 0.5;
        if (tw < STAGE_NAV_EDGE || tw > 1 - STAGE_NAV_EDGE) return;
      }
      var wrap = e.target && e.target.closest && e.target.closest(".odx-cinema2__media-wrap");
      if (!wrap || !this.contains(wrap)) return;
      var slide = wrap.closest && wrap.closest(".odx-cinema2__slide");
      if (!slide || slide !== this.slides[this.current]) return;
      var v = slide.querySelector("video.odx-cinema2__media");
      if (!v) return;
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var didRequestPlay = false;
      var playPromise = null;
      try {
        if (v.paused || v.ended) {
          if (v.ended) v.currentTime = 0;
          playPromise = v.play();
          didRequestPlay = true;
          if (playPromise && typeof playPromise.then === "function") {
            playPromise.then(
              function () {
                self._syncCinema2UiPlayState(true);
              },
              function () {
                self._syncCinema2UiPlayState(true);
              },
            );
          }
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(function () {});
          }
        } else {
          v.pause();
        }
      } catch (err) {
        didRequestPlay = false;
      }
      if (!didRequestPlay) {
        this._syncCinema2UiPlayState(true);
      } else if (!playPromise || typeof playPromise.then !== "function") {
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(function () {
            self._syncCinema2UiPlayState(true);
          });
        } else {
          window.setTimeout(function () {
            self._syncCinema2UiPlayState(true);
          }, 0);
        }
      }
    }

    _syncCinema2MuteGlyphs(btn, v, useAnim) {
      if (!btn || !v) return;
      var on = btn.querySelector(".odx-cinema2-ui__glyph--volume-on");
      var off = btn.querySelector(".odx-cinema2-ui__glyph--volume-off");
      var muted = !!v.muted;
      /* State affordance: muted → strike; unmuted → waves (tap still toggles mute). */
      if (on && off) {
        if (muted) {
          odxC2SwapTransportGlyphs(on, off, !!useAnim);
        } else {
          odxC2SwapTransportGlyphs(off, on, !!useAnim);
        }
      }
      btn.setAttribute("aria-label", muted ? "Unmute" : "Mute");
      this._notifyTransportDebug("syncCinema2MuteGlyphs");
    }

    _syncAllFsGlyphs(useAnim) {
      if (!this._section || typeof document === "undefined") return;
      var fsEl = odxC2GetFullscreenElement();
      var inFs = fsEl === this._section;
      var nodes = this._section.querySelectorAll("[data-odx-c2-ui-fs]");
      for (var i = 0; i < nodes.length; i++) {
        this._syncOneFsBtn(nodes[i], !!inFs, useAnim);
      }
      var slides = this.slides;
      var cur = this.current;
      if (slides) {
        for (var s = 0; s < slides.length; s++) {
          var w = slides[s].querySelector(".odx-cinema2__transport");
          if (!w) continue;
          w.classList.toggle("is-fullscreen", !!inFs && s === cur);
        }
      }
      this._notifyTransportDebug("syncAllFsGlyphs");
    }

    _syncOneFsBtn(btn, inFs, useAnim) {
      if (!btn) return;
      var enter = btn.querySelector(".odx-cinema2-ui__glyph--fs-enter");
      var exit = btn.querySelector(".odx-cinema2-ui__glyph--fs-exit");
      if (!enter || !exit) return;
      if (inFs) {
        odxC2SwapTransportGlyphs(enter, exit, !!useAnim);
      } else {
        odxC2SwapTransportGlyphs(exit, enter, !!useAnim);
      }
      btn.setAttribute("aria-label", inFs ? "Exit fullscreen" : "Fullscreen");
    }

    _notifyTransportDebug(reason, detail) {
      if (typeof window.odxCinema2TransportDebugNotify !== "function") return;
      try {
        window.odxCinema2TransportDebugNotify(reason, this, detail);
      } catch (eDbg) {
        /* ignore */
      }
    }

    _syncCinema2UiPlayState(useAnim) {
      var slide = this.slides[this.current];
      if (!slide) return;
      var btn = slide.querySelector("[data-odx-c2-ui-play]");
      if (!btn) return;
      var v = slide.querySelector("video.odx-cinema2__media");
      var playing = !!(v && !v.paused && !v.ended);
      btn.classList.toggle("is-playing", playing);
      btn.setAttribute("aria-label", playing ? "Pause" : "Play");
      var gp = btn.querySelector(".odx-cinema2-ui__glyph--play");
      var gq = btn.querySelector(".odx-cinema2-ui__glyph--pause");
      if (gp && gq) {
        if (playing) {
          odxC2SwapTransportGlyphs(gp, gq, !!useAnim);
        } else {
          odxC2SwapTransportGlyphs(gq, gp, !!useAnim);
        }
      }
      var mb = slide.querySelector("[data-odx-c2-ui-mute]");
      if (mb && v) {
        mb.classList.toggle("is-muted", !!v.muted);
        this._syncCinema2MuteGlyphs(mb, v, false);
      }
      odxC2MirrorTransportBem(slide, v);
      this._notifyTransportDebug("syncCinema2UiPlayState", {
        playing: playing,
        videoPaused: v ? !!v.paused : null,
        videoEnded: v ? !!v.ended : null,
      });
    }

    _onStageClick(e) {
      var nReal = this.originalSlideCount || this.slides.length;
      if (nReal < 2 || !this.stage) return;
      if (e.target && e.target.closest && e.target.closest("[data-odx-cinema2-nav], a, button, [role='button']")) {
        return;
      }
      var r = this.stage.getBoundingClientRect();
      var x = e.clientX - r.left;
      var t = r.width > 0 ? x / r.width : 0.5;
      if (t < STAGE_NAV_EDGE) this.prev();
      else if (t > 1 - STAGE_NAV_EDGE) this.next();
    }

    _onVisibilityChange() {
      if (document.hidden) {
        this._pauseAutoplay();
        this._clearImmersiveTimer();
        if (this._section) this._section.classList.remove("is-immersive");
      } else {
        this._resumeAutoplay();
        this._resetImmersiveTimer();
      }
    }

    _immersiveIdleEligible() {
      return !!(this.isVideoPlaying || this._activeSlideVideoIsPlaying());
    }

    _clearImmersiveTimer() {
      if (this._immersiveTimer != null) {
        window.clearTimeout(this._immersiveTimer);
        this._immersiveTimer = null;
      }
    }

    _resetImmersiveTimer() {
      if (!this._section) return;
      this._section.classList.remove("is-immersive");
      this._clearImmersiveTimer();
      if (!this._immersiveIdleEligible()) return;
      var self = this;
      this._immersiveTimer = window.setTimeout(function () {
        self._immersiveTimer = null;
        if (!self._section || !self._immersiveIdleEligible()) return;
        self._section.classList.add("is-immersive");
      }, this._immersiveIdleMs);
    }

    _setupImmersiveMode() {
      if (this._immersiveListeners || !this._section) return;
      var self = this;
      var raw = this.getAttribute("data-odx-immersive-ms");
      var ms = raw == null ? NaN : parseInt(raw, 10);
      this._immersiveIdleMs = ms === ms && ms >= 2000 && ms <= 9500 ? ms : IMMERSIVE_IDLE_MS_DEFAULT;
      this._onImmersivePointer = function () {
        self._resetImmersiveTimer();
      };
      this._onImmersiveKey = function (e) {
        if (!self._section) return;
        var t = e.target;
        if (t && typeof self._section.contains === "function" && self._section.contains(t)) {
          self._resetImmersiveTimer();
          return;
        }
        if (self._section.classList.contains("is-immersive")) {
          self._resetImmersiveTimer();
        }
      };
      this._section.addEventListener("mousemove", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
      this._section.addEventListener("mousedown", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
      this._section.addEventListener("touchstart", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
      window.addEventListener("keydown", this._onImmersiveKey, false);
      this._immersiveListeners = true;
      this._resetImmersiveTimer();
    }

    _teardownImmersiveMode() {
      this._clearImmersiveTimer();
      if (this._section) {
        this._section.classList.remove("is-immersive");
      }
      if (!this._immersiveListeners) {
        this._onImmersivePointer = null;
        this._onImmersiveKey = null;
        return;
      }
      if (this._section && this._onImmersivePointer) {
        this._section.removeEventListener("mousemove", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
        this._section.removeEventListener("mousedown", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
        this._section.removeEventListener("touchstart", this._onImmersivePointer, IMMERSIVE_PTR_OPTS);
      }
      if (this._onImmersiveKey) {
        window.removeEventListener("keydown", this._onImmersiveKey, false);
      }
      this._onImmersivePointer = null;
      this._onImmersiveKey = null;
      this._immersiveListeners = false;
    }

    _installVideoAutoplayInterlock() {
      if (!this.track || this._videoInterlockInstalled) return;
      var self = this;
      this._videoInterlockFn = function (e) {
        self._onVideoAutoplayInterlock(e);
      };
      this.track.addEventListener("play", this._videoInterlockFn, true);
      this.track.addEventListener("pause", this._videoInterlockFn, true);
      this.track.addEventListener("ended", this._videoInterlockFn, true);
      this.track.addEventListener("error", this._videoInterlockFn, true);
      this.track.addEventListener("volumechange", this._videoInterlockFn, true);
      this._videoInterlockInstalled = true;
    }

    _teardownVideoAutoplayInterlock() {
      if (!this.track || !this._videoInterlockFn) return;
      this.track.removeEventListener("play", this._videoInterlockFn, true);
      this.track.removeEventListener("pause", this._videoInterlockFn, true);
      this.track.removeEventListener("ended", this._videoInterlockFn, true);
      this.track.removeEventListener("error", this._videoInterlockFn, true);
      this.track.removeEventListener("volumechange", this._videoInterlockFn, true);
      this._videoInterlockInstalled = false;
      this._videoInterlockFn = null;
    }

    /**
     * True when the physically active slide’s cinema video is mid-play (covers gap before `play` after `play()`).
     * Decode / source errors are treated as idle so autoplay is not wedged. A long `stalled` while !paused is
     * intentionally still “playing” (buffering); revisit with a timeupdate watchdog if that proves painful in the field.
     */
    _activeSlideVideoIsPlaying() {
      var slides = this.slides;
      var idx = this.current;
      if (!slides || idx < 0 || idx >= slides.length) return false;
      var slide = slides[idx];
      if (!slide || slide.hasAttribute("data-odx-clone")) return false;
      var v = slide.querySelector("video.odx-cinema2__media");
      if (!v || v.error) return false;
      return !v.paused && !v.ended;
    }

    _onVideoAutoplayInterlock(e) {
      var t = e.target;
      if (!t || t.tagName !== "VIDEO" || !t.classList || !t.classList.contains("odx-cinema2__media")) return;
      var slide = t.closest && t.closest(".odx-cinema2__slide");
      if (!slide) return;
      var slides = this.slides;
      if (!slides || this.current < 0 || this.current >= slides.length) return;
      if (slide !== slides[this.current]) return;
      var isCloneSlide = slide.hasAttribute("data-odx-clone");

      if (e.type === "volumechange") {
        var mb0 = slide.querySelector("[data-odx-c2-ui-mute]");
        if (mb0) {
          mb0.classList.toggle("is-muted", !!t.muted);
          this._syncCinema2MuteGlyphs(mb0, t, false);
        }
        odxC2MirrorTransportBem(slide, t);
        return;
      }

      if (e.type === "play") {
        this.isVideoPlaying = true;
        this._autoplaySuspendedUntilNav = false;
        this._syncCinema2UiPlayState();
        this._resetImmersiveTimer();
        return;
      }
      if (e.type === "error") {
        this.isVideoPlaying = false;
        this._syncCinema2UiPlayState();
        this._resetImmersiveTimer();
        this._restartAutoplayIfNeeded();
        return;
      }
      this.isVideoPlaying = false;
      if (e.type === "ended") {
        if (isCloneSlide) return;
        this._autoplaySuspendedUntilNav = false;
        try {
          t.pause();
        } catch (err) {
          /* ignore */
        }
        this._syncCinema2UiPlayState();
        /* `next()` → `goToSlidePhysical` restarts the autoplay interval (fresh full delay on the new slide). */
        this.next();
        return;
      }
      if (e.type === "pause") {
        if (t.ended) return;
        if (!isCloneSlide) {
          this._autoplaySuspendedUntilNav = true;
          this._stopAutoplay();
        }
        this._syncCinema2UiPlayState();
        this._resetImmersiveTimer();
      }
    }

    _startAutoplay() {
      var self = this;
      this._stopAutoplay();
      var nReal = this.originalSlideCount || this.slides.length;
      if (!this._autoplay || nReal < 2) return;
      if (this._autoplaySuspendedUntilNav) return;
      this._tick = window.setInterval(function () {
        if (self.isVideoPlaying || self._activeSlideVideoIsPlaying()) return;
        if (self._autoplaySuspendedUntilNav) return;
        self.next();
      }, this._intervalMs);
    }

    _pauseAutoplay() {
      if (this._tick != null) {
        window.clearInterval(this._tick);
        this._tick = null;
      }
    }

    _resumeAutoplay() {
      var nReal = this.originalSlideCount || this.slides.length;
      if (!this._autoplay || nReal < 2 || document.hidden) return;
      if (this._autoplaySuspendedUntilNav) return;
      this._startAutoplay();
    }

    _stopAutoplay() {
      this._pauseAutoplay();
    }

    _restartAutoplayTimer() {
      var nReal = this.originalSlideCount || this.slides.length;
      if (!this._autoplay || nReal < 2) return;
      this._startAutoplay();
    }

    _restartAutoplayIfNeeded() {
      if (!this._autoplay) return;
      var nReal = this.originalSlideCount || this.slides.length;
      if (nReal < 2) {
        this._stopAutoplay();
        return;
      }
      if (!document.hidden && !this._autoplaySuspendedUntilNav) this._startAutoplay();
    }

    refreshLayout() {
      this.track = this.track || this.querySelector("[data-odx-cinema2-track]");
      this._installVideoAutoplayInterlock();
      var logicalBefore = typeof this.physicalToLogical === "function" ? this.physicalToLogical(this.current) : 0;
      var hadClones = this.cloneLead > 0;

      this.refreshSlidesFromDom();
      this._removeInfiniteClones();
      this.refreshSlidesFromDom();
      this._buildInfiniteClones();

      var track = this.track;
      var slides = this.slides;
      var tot = slides.length;
      if (!track || tot < 1) return;
      track.style.setProperty("--odx-cinema2-slides", String(tot));
      track.style.width = tot * 100 + "%";

      var k = this.cloneLead;
      var nReal = this.originalSlideCount;
      if (k && nReal > 1) {
        if (hadClones && !this._seedInfiniteStart) {
          var lg = Math.max(0, Math.min(nReal - 1, logicalBefore));
          this.current = k + lg;
        } else if (this._seedInfiniteStart || this.current < k) {
          this.current = k;
          this._seedInfiniteStart = false;
        }
      } else {
        if (this.current >= tot) this.current = tot - 1;
      }

      this._ensureThumbTrackRef();
      this._layoutThumbnailTrack(nReal > 0 ? nReal : tot);
      this._ensureNavAndInteractions();
      this._ensureAutoplayListeners();
      this.updatePosition();
      this._restartAutoplayIfNeeded();
      this._stopUpcomingSignificantCountdown();
      this._startUpcomingSignificantCountdown();
    }

    _ensureThumbTrackRef() {
      if (this._thumbTrack && this._thumbViewport) return;
      var sec = this.closest("[data-odx-cinema2]");
      if (!sec) return;
      if (!this._thumbTrack) {
        this._thumbTrack = sec.querySelector("[data-odx-cinema2-thumbnails-track]");
      }
      if (!this._thumbViewport) {
        this._thumbViewport = sec.querySelector("[data-odx-cinema2-thumbnails-viewport]");
      }
    }

    _layoutThumbnailTrack(nReal) {
      var tt = this._thumbTrack;
      if (!tt || nReal < 1) return;
      var totalW = nReal * THUMB_CELL + Math.max(0, nReal - 1) * THUMB_GAP;
      tt.style.width = totalW + "px";
    }

    updatePosition() {
      var track = this.track;
      var slides = this.slides;
      var tot = slides.length;
      if (!track || tot < 1) return;
      var idx = this.current;
      if (idx < 0) idx = 0;
      if (idx >= tot) idx = tot - 1;
      this.current = idx;
      var pct = (idx * 100) / tot;
      track.style.transform = "translate3d(-" + pct + "%, 0, 0)";
      for (var s = 0; s < slides.length; s++) {
        slides[s].classList.toggle("is-cinema-active", s === idx);
      }
      this._syncSlideVideos();
      this._syncThumbnailStripFromPhysical();
      this._resetImmersiveTimer();
    }

    _syncThumbnailStripFromPhysical() {
      this._ensureThumbTrackRef();
      var tt = this._thumbTrack;
      var nReal = this.originalSlideCount;
      if (!tt || nReal < 1) return;
      this._layoutThumbnailTrack(nReal);
      var logical = this.physicalToLogical(this.current);
      if (logical < 0) logical = 0;
      if (logical >= nReal) logical = nReal - 1;
      var step = THUMB_CELL + THUMB_GAP;
      var trackW = nReal * THUMB_CELL + Math.max(0, nReal - 1) * THUMB_GAP;
      var vp = this._thumbViewport;
      var W =
        vp && typeof vp.clientWidth === "number" && vp.clientWidth > 0
          ? vp.clientWidth
          : REMOTE_THUMB_VIEWPORT_FALLBACK;
      var thumbCenter = logical * step + THUMB_CELL / 2;
      var offset = W / 2 - thumbCenter;
      if (trackW <= W) {
        offset = (W - trackW) / 2;
      } else {
        var minOff = W - trackW;
        var maxOff = 0;
        offset = Math.max(minOff, Math.min(maxOff, offset));
      }
      tt.style.transform = "translate3d(" + offset + "px, 0, 0)";
      var thumbs = tt.querySelectorAll("[data-odx-cinema2-thumb]");
      for (var t = 0; t < thumbs.length; t++) {
        if (t === logical) thumbs[t].classList.add("is-active");
        else thumbs[t].classList.remove("is-active");
      }
    }

    _maybeSnapInfinite() {
      var k = this.cloneLead;
      var n = this.originalSlideCount;
      if (!k || n < 2) return;
      var p = this.current;
      if (p >= k + n && p < k + n + k) {
        var i = p - (k + n);
        this._snapToPhysical(k + i);
        return;
      }
      if (p < k) {
        this._snapToPhysical(k + (n - k + p));
      }
    }

    _snapToPhysical(target) {
      var track = this.track;
      var slides = this.slides;
      if (!track || slides.length < 1 || typeof target !== "number") return;
      var tot = slides.length;
      if (target < 0) target = 0;
      if (target >= tot) target = tot - 1;
      var prev = track.style.transition;
      track.style.transition = "none";
      this.current = target;
      var pct = (target * 100) / tot;
      track.style.transform = "translate3d(-" + pct + "%, 0, 0)";
      void track.offsetWidth;
      track.style.transition = prev || "";
      for (var s = 0; s < slides.length; s++) {
        slides[s].classList.toggle("is-cinema-active", s === target);
      }
      this._syncSlideVideos();
      this._syncThumbnailStripFromPhysical();
      this._resetImmersiveTimer();
    }

    _syncSlideVideos() {
      var slides = this.slides;
      var idx = this.current;
      this.isVideoPlaying = false;
      /* No `video.odx-cinema2__media` on teaser-only released slides (playback hold) — loop is a no-op. */
      for (var i = 0; i < slides.length; i++) {
        var v = slides[i].querySelector("video.odx-cinema2__media");
        if (!v) continue;
        var isClone = slides[i].hasAttribute && slides[i].hasAttribute("data-odx-clone");
        try {
          if (!isClone && i === idx) {
            var p = v.play();
            if (p && typeof p.catch === "function") p.catch(function () {});
          } else {
            v.pause();
          }
        } catch (err) {
          /* ignore */
        }
      }
      this._syncCinema2UiPlayState();
    }

    /** Public: logical index 0..N-1 (matches thumbnail data-slide-index). */
    goToSlide(index) {
      if (typeof index !== "number" || index !== index) return;
      var n = this.originalSlideCount || this.slides.length;
      if (n < 1) return;
      if (index < 0 || index >= n) return;
      this._autoplaySuspendedUntilNav = false;
      var k = this.cloneLead || 0;
      this.current = index + k;
      this.updatePosition();
      this._restartAutoplayTimer();
    }

    goToSlidePhysical(p) {
      var tot = this.slides.length;
      if (tot < 1) return;
      this._autoplaySuspendedUntilNav = false;
      var idx = ((p % tot) + tot) % tot;
      this.current = idx;
      this.updatePosition();
      this._restartAutoplayTimer();
    }

    next() {
      var tot = this.slides.length;
      if (tot < 1) return;
      this.goToSlidePhysical(this.current + 1);
    }

    prev() {
      var tot = this.slides.length;
      if (tot < 1) return;
      this.goToSlidePhysical(this.current - 1);
    }
  }

  if (!customElements.get("odx-cinema2-carousel")) {
    customElements.define("odx-cinema2-carousel", OdxCinema2Carousel);
  }

  function wireSection(section) {
    if (!section || section.getAttribute("data-odx-cinema2-wired") === "1") return;
    var carousel = section.querySelector("odx-cinema2-carousel");
    if (!carousel || !carousel.track) return;
    if (typeof carousel.refreshSlidesFromDom === "function") {
      carousel.refreshSlidesFromDom();
    }
    if (typeof carousel.refreshLayout === "function") {
      carousel.refreshLayout();
    }
    if (carousel.slides.length < 1) return;
    section.setAttribute("data-odx-cinema2-wired", "1");
    installDocumentArrowNav();

    var sid = section.getAttribute("id") || "";
    window.odxCinema2Registry = window.odxCinema2Registry || {};
    window.odxCinema2Registry[sid] = {
      goToSlide: function (i) {
        carousel.goToSlide(i);
      },
      next: function () {
        carousel.next();
      },
      prev: function () {
        carousel.prev();
      },
      getIndex: function () {
        return typeof carousel.getLogicalIndex === "function" ? carousel.getLogicalIndex() : carousel.current;
      },
      setTheaterMode: function (on) {
        if (on) section.classList.add("is-fullscreen");
        else section.classList.remove("is-fullscreen");
      },
    };
  }

  function syncCinema2FullscreenClass() {
    var el = document.fullscreenElement;
    document.querySelectorAll("[data-odx-cinema2].is-fullscreen").forEach(function (sec) {
      sec.classList.remove("is-fullscreen");
    });
    if (!el) return;
    document.querySelectorAll("[data-odx-cinema2]").forEach(function (sec) {
      if (sec.contains(el)) sec.classList.add("is-fullscreen");
    });
  }

  function boot() {
    document.querySelectorAll("[data-odx-cinema2]").forEach(wireSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  setTimeout(boot, 0);

  document.addEventListener("fullscreenchange", syncCinema2FullscreenClass, false);

  document.addEventListener("shopify:section:load", function (ev) {
    var sec = ev && ev.target;
    if (!sec || !sec.querySelector) return;
    var inner = sec.querySelector("[data-odx-cinema2]");
    if (inner) {
      inner.removeAttribute("data-odx-cinema2-wired");
      wireSection(inner);
    }
  });
})();
